import { Injectable } from '@angular/core';
import { Observable, of, delay, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { 
  AgentHubItem, 
  AgentCategory, 
  AgentSearchResponse, 
  AgentFavoriteResponse,
} from '../../models';
import { AgentConfigService } from '../agent-config/agent-config.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Agent Hub Service
 * 
 * Manages agent hub data including:
 * - Loading and transforming agent data from AgentConfigService
 * - Search functionality with mock API simulation
 * - Favorite agent management with local storage persistence
 * 
 * Note: Search and favorite APIs are mocked and will be replaced
 * with real API endpoints in the future.
 * 
 * @Injectable providedIn: 'root'
 */
@Injectable({
  providedIn: 'root'
})
export class AgentHubService {
  /** Mock delay for API simulation (milliseconds) */
  private readonly MOCK_API_DELAY = 300;

  /** Local storage key for favorites */
  private readonly FAVORITES_STORAGE_KEY = 'agent_hub_favorites';

  /** BehaviorSubject to track favorite agent IDs */
  private readonly favoritesSubject = new BehaviorSubject<string[]>(this.loadFavoritesFromStorage());

  /** Observable stream of favorite agent IDs */
  public favorites$ = this.favoritesSubject.asObservable();

  /** 
   * Allowed agent IDs from feature flags (enrolled_agents intersection with ACL)
   * When set, only these agents will be shown in the hub
   * When null, all enabled agents from config will be shown (fallback)
   */
  private allowedAgentIds: string[] | null = null;

  /** Default categories for agents */
  private readonly defaultCategories: AgentCategory[] = [
    { id: 'PAC', name: 'VIA_AGENT.VIA_AGENT_TAG', colorVariant: 'blue' },
    { id: 'Regulatory', name: 'REGULATIONS_AGENT.REGULATORY_AGENT_TAG', colorVariant: 'green' },
    { id: 'Ria', name: 'RIA_AGENT.RIA_AGENT_TAG', colorVariant: 'orange' },
    { id: 'reports', name: 'Reports', colorVariant: 'orange' },
    { id: 'documents', name: 'Documents', colorVariant: 'purple' }
  ];

  constructor(
    private readonly agentConfigService: AgentConfigService,
    private logger: LoggerService,
    private translate: TranslateService
  ) {}

  /**
   * Set allowed agent IDs from feature flags
   * Called by AgentWorkspaceComponent after receiving the intersection
   * of enrolled agents and ACL-validated agents from ChatbotWrapperComponent
   * 
   * @param agentIds - Array of agent IDs that are allowed by feature flags
   */
  setAllowedAgentIds(agentIds: string[]): void {
    this.allowedAgentIds = agentIds;
    this.logger.logInfo(`[AgentHubService] Allowed agent IDs set from feature flags: [${agentIds.join(', ')}]`, 'setAllowedAgentIds');
  }

  /**
   * Get allowed agent IDs (for debugging/verification)
   * 
   * @returns string[] | null - Allowed agent IDs or null if not set
   */
  getAllowedAgentIds(): string[] | null {
    return this.allowedAgentIds;
  }

  /**
   * Clear allowed agent IDs (reset to show all enabled agents)
   */
  clearAllowedAgentIds(): void {
    this.allowedAgentIds = null;
    this.logger.logInfo('[AgentHubService] Allowed agent IDs cleared', 'clearAllowedAgentIds');
  }

  /**
   * Get all available categories
   * 
   * @returns AgentCategory[] - Array of all defined categories
   */
  getCategories(): AgentCategory[] {
    return [...this.defaultCategories];
  }

  /**
   * Get categories dynamically based on agents that are actually loaded.
   * Only returns categories that have at least one agent assigned to them.
   * This ensures the filter tabs only show relevant categories.
   * 
   * @param agents - Array of AgentHubItems currently loaded
   * @returns AgentCategory[] - Array of categories that have at least one agent
   */
  getCategoriesFromAgents(agents: AgentHubItem[]): AgentCategory[] {
    // Collect unique category IDs from the loaded agents
    const uniqueCategoryIds = new Set<string>(
      agents.map(agent => agent.category.id)
    );

    // Return only the categories that exist among the loaded agents,
    // preserving the original order from defaultCategories
    const dynamicCategories = this.defaultCategories.filter(
      category => uniqueCategoryIds.has(category.id)
    );

    this.logger.logInfo(
      `[AgentHubService] Dynamic categories derived from ${agents.length} agents: [${dynamicCategories.map(c => c.id).join(', ')}]`,
      'getCategoriesFromAgents'
    );

    return dynamicCategories;
  }

  /**
   * Get category by ID or i18n name key from agent config (e.g. "PAC", "Ria", or "REGULATIONS_AGENT.REGULATORY_AGENT_TAG")
   * 
   * @param categoryId - Category ID or translation key from agent config
   * @returns AgentCategory | undefined
   */
  getCategoryById(categoryId: string): AgentCategory | undefined {
    return this.defaultCategories.find(c => c.id === categoryId || c.name === categoryId);
  }

  /**
   * Get all agents as AgentHubItems
   * Transforms AgentConfig data to AgentHubItem format
   * Filters by allowedAgentIds when set (from feature flags)
   * 
   * @returns Observable<AgentHubItem[]>
   */
  getAllAgents(): Observable<AgentHubItem[]> {
    try {
      let agents = this.agentConfigService.getEnabledAgents();
      const favorites = this.favoritesSubject.getValue();
      
      // Filter by allowed agent IDs from feature flags (if set)
      if (this.allowedAgentIds !== null && this.allowedAgentIds.length > 0) {
        const originalCount = agents.length;
        agents = agents.filter(agent => this.allowedAgentIds!.includes(agent.id));
        this.logger.logInfo(
          `[AgentHubService] Filtering agents by feature flags: ${originalCount} → ${agents.length} agents. Allowed: [${this.allowedAgentIds.join(', ')}]`,
          'getAllAgents'
        );
      }
      
      const hubItems: AgentHubItem[] = agents.map((agent, index) => {
        // Assign category from agent config, fallback to default if not specified
        const category = this.assignCategory(agent.category, index);
        
        // Debug: Log agent popup data
        console.log(`[AgentHubService] Agent ${agent.id} popup data:`, {
          about: agent.about,
          version: agent.version,
          versionNotes: agent.versionNotes,
          features: agent.features
        });
        
        return {
          id: agent.id,
          name: agent.display_name,
          description: agent.landing_page?.short_intro || agent.landing_page?.intro || `${agent.display_name} helps you navigate regional regulatory requirements.`,
          icon: agent.icon,
          category: category,
          isFavorite: favorites.includes(agent.id),
          enabled: agent.enabled,
          // Include popup details from agent config
          about: agent.about,
          version: agent.version,
          versionNotes: agent.versionNotes,
          features: agent.features
        };
      });

      return of(hubItems);
    } catch (error: any) {
      this.logger.logInfo('[AgentHubService] Error getting all agents', 'getAllAgents', error);
      return of([]);
    }
  }

  /**
   * Search agents by query string
   * Mock API - will be replaced with real endpoint
   * 
   * @param query - Search query string
   * @returns Observable<AgentSearchResponse>
   */
  searchAgents(query: string): Observable<AgentSearchResponse> {
    try {
      this.logger.logInfo(`[AgentHubService] Searching agents with query: "${query}"`, 'searchAgents');
      
      return this.getAllAgents().pipe(
        delay(this.MOCK_API_DELAY), // Simulate API latency
        map(agents => {
          const lowerQuery = query.toLowerCase().trim();
          
          // If empty query, return all agents
          if (!lowerQuery) {
            return {
              agents,
              totalCount: agents.length,
              query
            };
          }

          // Filter agents by name, description, or category (translate before comparing)
          const filteredAgents = agents.filter(agent => {
            const translatedName = this.translate.instant(agent.name).toLowerCase();
            const translatedDescription = this.translate.instant(agent.description).toLowerCase();
            const translatedCategoryName = this.translate.instant(agent.category.name).toLowerCase();
            return translatedName.includes(lowerQuery) ||
              translatedDescription.includes(lowerQuery) ||
              translatedCategoryName.includes(lowerQuery);
          });

          return {
            agents: filteredAgents,
            totalCount: filteredAgents.length,
            query
          };
        })
      );
    } catch (error: any) {
      this.logger.logInfo('[AgentHubService] Error searching agents', 'searchAgents', error);
      return of({ agents: [], totalCount: 0, query });
    }
  }

  /**
   * Toggle favorite status for an agent
   * Mock API - will be replaced with real endpoint
   * 
   * @param agentId - ID of the agent to toggle
   * @returns Observable<AgentFavoriteResponse>
   */
  toggleFavorite(agentId: string): Observable<AgentFavoriteResponse> {
    try {
      this.logger.logInfo(`[AgentHubService] Toggling favorite for agent: ${agentId}`, 'toggleFavorite');
      
      const currentFavorites = this.favoritesSubject.getValue();
      const isFavorite = currentFavorites.includes(agentId);
      
      let newFavorites: string[];
      if (isFavorite) {
        // Remove from favorites
        newFavorites = currentFavorites.filter(id => id !== agentId);
      } else {
        // Add to favorites
        newFavorites = [...currentFavorites, agentId];
      }

      // Update subject and persist
      this.favoritesSubject.next(newFavorites);
      this.saveFavoritesToStorage(newFavorites);

      // Simulate API delay
      return of({
        agentId,
        isFavorite: !isFavorite,
        success: true
      }).pipe(delay(this.MOCK_API_DELAY));
    } catch (error: any) {
      this.logger.logInfo('[AgentHubService] Error toggling favorite', 'toggleFavorite', error);
      return of({ agentId, isFavorite: false, success: false });
    }
  }

  /**
   * Get current favorite agent IDs
   * 
   * @returns Observable<string[]>
   */
  getFavorites(): Observable<string[]> {
    return this.favorites$;
  }

  /**
   * Check if an agent is favorited
   * 
   * @param agentId - Agent ID to check
   * @returns boolean
   */
  isFavorite(agentId: string): boolean {
    return this.favoritesSubject.getValue().includes(agentId);
  }

  /**
   * Get agents filtered by category
   * 
   * @param categoryId - Category ID to filter by ('all' for all agents)
   * @returns Observable<AgentHubItem[]>
   */
  getAgentsByCategory(categoryId: string): Observable<AgentHubItem[]> {
    return this.getAllAgents().pipe(
      map(agents => {
        if (categoryId === 'all') {
          return agents;
        }
        return agents.filter(agent => agent.category.id === categoryId);
      })
    );
  }

  /**
   * Assign a category to an agent based on its category from config
   * Uses the category defined in agent_config.json
   * 
   * @param categoryId - Category ID from agent config (e.g., "regulations", "reports", "summarize", "documents")
   * @param index - Agent index in array (used as fallback)
   * @returns AgentCategory
   */
  private assignCategory(categoryId: string | undefined, index: number): AgentCategory {
    // Use category from agent config if provided
    if (categoryId) {
      const category = this.getCategoryById(categoryId);
      if (category) {
        return category;
      }
      this.logger.logInfo(`[AgentHubService] Unknown category "${categoryId}", using fallback`, 'assignCategory');
    }

    // Fallback: cycle through categories based on index
    return this.defaultCategories[index % this.defaultCategories.length];
  }

  /**
   * Load favorites from local storage
   * 
   * @returns string[] - Array of favorite agent IDs
   */
  private loadFavoritesFromStorage(): string[] {
    try {
      const stored = localStorage.getItem(this.FAVORITES_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHubService] Error loading favorites from storage', 'loadFavoritesFromStorage', error);
    }
    return [];
  }

  /**
   * Save favorites to local storage
   * 
   * @param favorites - Array of favorite agent IDs to save
   */
  private saveFavoritesToStorage(favorites: string[]): void {
    try {
      localStorage.setItem(this.FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (error: any) {
      this.logger.logInfo('[AgentHubService] Error saving favorites to storage', 'saveFavoritesToStorage', error);
    }
  }
}
